import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { $transferUpdateStatusStore } from '@lib/store';
import { RestApiError } from '@shared/api/utils';
import { TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api/rest/transferRequests';
import { OTPInput } from '../input';
import { Button, ErrorMessage, Spinner, Text } from '@shared/widgets';

const OTP_LENGTH = 6;

interface IForm {
  otpCode: string;
}
const DEFAULT_VALUES: IForm = { otpCode: '' };

interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TransferRequestResponse;
  onSubmit?: (data?: TransferRequestResponse) => void;
  onCancel?: () => void;
}

export const OTPModal = (props: OTPModalProps) => {
  const formId = useId();
  const { control, formState, handleSubmit, setError, watch, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const otpCode = watch('otpCode');

  const { t } = useTranslation('payments');

  const submitHandler = async (data: IForm) => {
    try {
      const response = await $transferUpdateStatusStore.events.update({
        id: props.data.id,
        otpCode: data.otpCode,
        status: TransferRequestStatusEnum.Completed,
      });
      reset(DEFAULT_VALUES);
      props.onSubmit?.(response.data);
      props.onClose();
    } catch (error) {
      if (RestApiError.isRestApiError(error)) {
        if (error.message === 'Invalid code, try again') {
          setError('otpCode', { message: t(`errors.api.${error.message}`, error.message) });
          return;
        }
      }
      reset(DEFAULT_VALUES);
      props.onClose();
    }
  };

  if (!props.isOpen) return;
  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50'>
      <div className='w-full max-w-lg bg-ice p-6 shadow-lg dark:bg-soft-black'>
        <Text className='mb-6 text-center' size='3xl'>
          {t('modals.otp.title')}
        </Text>
        <form id={formId} className='mb-10 flex justify-center' noValidate onSubmit={handleSubmit(submitHandler)}>
          {formState.isSubmitting ? (
            <Spinner />
          ) : (
            <Controller
              name='otpCode'
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className='flex flex-col gap-1'>
                  <OTPInput length={OTP_LENGTH} error={!!error?.message} value={value} onChange={onChange} />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
          )}
        </form>
        <div className='flex gap-3'>
          <Button
            fullWidth
            color='outlined'
            disabled={formState.isSubmitting}
            onClick={() => (props.onCancel?.() || props.onClose(), reset(DEFAULT_VALUES))}
          >
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button fullWidth color='primary' type='submit' form={formId} disabled={formState.isSubmitting || otpCode.length !== OTP_LENGTH}>
            {t('modals.otp.buttons.confirmCode')}
          </Button>
        </div>
      </div>
    </div>
  );
};
